/**
 * NPS auto-prompt (T211).
 *
 * Shows the NPS dialog on the dashboard at two milestones:
 *   - 14-day mark (the "is this useful enough to keep using" question)
 *   - 60-day mark (the "is this useful enough to keep paying for" question)
 *
 * Trigger is purely client-side: SPA reads `user.date_joined`, computes
 * days-since-signup, and shows the dialog when the count hits the
 * milestone AND the operator hasn't already answered or dismissed it.
 * Per-device dedup via localStorage so we don't pester users who've
 * already given us a score.
 *
 * On submit, the dialog calls `startNpsFeedback(score, comment)` from
 * the T203 feedback API client — the score lands as a FeedbackThread
 * row with nps_score populated, persona-tagged "scorer" automatically
 * via detectPersona() (the user's on /dashboard at the time).
 *
 * Why not server-side cron + push: client-side is one file vs new model
 * + migration + management command + cron config. The trade-off is
 * per-device dedup (someone who answers on phone gets prompted again
 * on laptop) — acceptable for this MVP since NPS isn't a daily ask.
 */
import { useEffect, useState } from "react";


const STORAGE_KEY = "zcd_nps_milestones";


function loadDone() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? new Set(JSON.parse(raw)) : new Set();
  } catch { return new Set(); }
}


function saveDone(set) {
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify([...set])); } catch {}
}


/** @param {Date} d / @returns days since `d` (rounded down) */
function daysSince(d) {
  const ms = Date.now() - d.getTime();
  return Math.floor(ms / (1000 * 60 * 60 * 24));
}


/**
 * @param {{ date_joined?: string | null } | null | undefined} user
 * @returns {{ milestone: "week_2" | "month_2" | null, dismiss: () => void, markDone: () => void }}
 */
export function useNpsPrompt(user) {
  const [milestone, setMilestone] = useState(null);

  useEffect(() => {
    if (!user?.date_joined) return undefined;
    const joined = new Date(user.date_joined);
    if (Number.isNaN(joined.getTime())) return undefined;
    const days = daysSince(joined);
    const done = loadDone();

    // Window of ±2 days around each milestone so a user who skips the
    // dashboard for a day doesn't miss the prompt forever.
    let next = null;
    if (days >= 14 && days <= 16 && !done.has("week_2")) {
      next = "week_2";
    } else if (days >= 60 && days <= 62 && !done.has("month_2")) {
      next = "month_2";
    }

    // Don't fire immediately on dashboard mount — give the user 2s to
    // orient before the dialog appears. Otherwise it feels intrusive.
    if (next) {
      const t = setTimeout(() => setMilestone(next), 2000);
      return () => clearTimeout(t);
    }
    return undefined;
  }, [user?.date_joined]);

  const dismiss = () => {
    const done = loadDone();
    if (milestone) {
      done.add(milestone);
      saveDone(done);
    }
    setMilestone(null);
  };

  const markDone = () => {
    const done = loadDone();
    if (milestone) {
      done.add(milestone);
      saveDone(done);
    }
    setMilestone(null);
  };

  return { milestone, dismiss, markDone };
}
